import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Loader2, Search, Inbox, Download, ChevronLeft, ChevronRight, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = ["new", "replied", "contacted", "closed", "spam"];
const STATUS_STYLES = {
  new: "bg-cyan-50 text-cyan-700 border-cyan-200",
  replied: "bg-emerald-50 text-emerald-700 border-emerald-200",
  contacted: "bg-indigo-50 text-indigo-700 border-indigo-200",
  closed: "bg-slate-100 text-slate-700 border-slate-200",
  spam: "bg-red-50 text-red-700 border-red-200",
};

export function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${STATUS_STYLES[status] || STATUS_STYLES.closed}`}
    >
      {status}
    </span>
  );
}

export default function AdminContactRequestsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10));
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [requestType, setRequestType] = useState(params.get("request_type") || "");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/contact-submissions`, {
        params: {
          page, per_page: 20,
          q: q || undefined,
          status: status || undefined,
          request_type: requestType || undefined,
        },
      });
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load contact requests");
    } finally {
      setLoading(false);
    }
  }, [page, q, status, requestType]);

  useEffect(() => { load(); }, [load]);

  // Sync URL
  useEffect(() => {
    const next = new URLSearchParams();
    if (page !== 1) next.set("page", String(page));
    if (q) next.set("q", q);
    if (status) next.set("status", status);
    if (requestType) next.set("request_type", requestType);
    setParams(next, { replace: true });
  }, [page, q, status, requestType, setParams]);

  const onExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/exports/contact-submissions.csv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "psytech_contact_submissions.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-contact-requests">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Contact Requests</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {total} total submission{total === 1 ? "" : "s"}
          </p>
        </div>
        <Button
          onClick={onExport}
          variant="outline"
          size="sm"
          data-testid="contact-export-csv"
        >
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search name, email, company, message…"
            data-testid="contact-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
          />
        </div>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          data-testid="contact-status-filter"
          className="text-sm rounded-lg border border-slate-200 px-3 py-2 bg-white"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={requestType}
          onChange={(e) => { setRequestType(e.target.value); setPage(1); }}
          data-testid="contact-request-type-filter"
          className="text-sm rounded-lg border border-slate-200 px-3 py-2 bg-white"
        >
          <option value="">All request types</option>
          <option value="contact">contact</option>
          <option value="general_inquiry">general_inquiry</option>
          <option value="demo_request">demo_request</option>
          <option value="pilot_inquiry">pilot_inquiry</option>
          <option value="clinic_pilot">clinic_pilot</option>
          <option value="education_pilot">education_pilot</option>
          <option value="company_pilot">company_pilot</option>
          <option value="partnership">partnership</option>
          <option value="investor">investor</option>
          <option value="privacy_question">privacy_question</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-700 bg-red-50">{error}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No submissions match your filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">Date</th>
                  <th className="px-4 py-2.5 font-semibold">Name</th>
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                  <th className="px-4 py-2.5 font-semibold">Subject</th>
                  <th className="px-4 py-2.5 font-semibold">Source</th>
                  <th className="px-4 py-2.5 font-semibold">Type</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="hover:bg-slate-50/70 cursor-pointer"
                    data-testid={`contact-row-${it.id}`}
                    onClick={() => navigate(`/admin/contact-requests/${it.id}`)}
                  >
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                      {new Date(it.created_at).toLocaleDateString()}<br />
                      <span className="text-[10px] text-slate-400">{new Date(it.created_at).toLocaleTimeString()}</span>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-900">{it.name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{it.email}</td>
                    <td className="px-4 py-2.5 text-slate-600 max-w-[280px] truncate">
                      {(it.message || "").slice(0, 80)}{(it.message || "").length > 80 ? "…" : ""}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{it.source_page || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
                        {it.request_type || "contact"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={it.status || "new"} />
                    </td>
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/admin/contact-requests/${it.id}`}
                        data-testid={`contact-open-${it.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-cyan-700 hover:text-cyan-900"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm">
            <div className="text-slate-500">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
